import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fetchOldAllMember } from "../Redux/Slice/AddMember";
import Loader from "../Components/Loader/Loader";

const InputFieldWithLabel = ({
  label,
  name,
  type = "text",
  placeholder,
  value,
  error,
  onChange,
}) => (
  <div className="col-span-12 md:col-span-6">
    <label className="block text-sm font-bold text-gray-900 mb-1">
      {label}
    </label>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47]"
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const InternshipCompletionLetter = () => {
  const dispatch = useDispatch();
  const { MemberOldAllData, MemberOldAllLoading } = useSelector(
    (state) => state.AddMemberSlice
  );

  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    date: "",
    internName: "",
    universityName: "",
    companyName: "",
    startDate: "",
    endDate: "",
    internshipInfo: "",
  });

  const [errors, setErrors] = useState({});
  const [generatedText, setGeneratedText] = useState("");
  const pdfContentRef = useRef(null);

  // Fetch employees on mount
  useEffect(() => {
    dispatch(fetchOldAllMember());
  }, [dispatch]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const calculateEndDate = (startDateStr, monthsStr) => {
    if (!startDateStr || !monthsStr) return "";
    const date = new Date(startDateStr);
    if (isNaN(date.getTime())) return "";
    const months = parseInt(monthsStr);
    if (isNaN(months)) return "";
    date.setMonth(date.getMonth() + months - 1);
    // Set to the last day of that month
    const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    nextMonth.setDate(nextMonth.getDate() - 1);
    const year = nextMonth.getFullYear();
    const month = String(nextMonth.getMonth() + 1).padStart(2, "0");
    const day = String(nextMonth.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Populate data when employee is selected
  const handleEmployeeSelect = (e) => {
    const empId = e.target.value;
    setSelectedEmpId(empId);
    setGeneratedText("");

    if (!empId) {
      resetForm();
      return;
    }

    const emp = MemberOldAllData?.data?.find((el) => el._id === empId);
    if (emp) {
      const start = formatDateForInput(emp.JoiningDate);
      const period = emp.internshiptime || "";
      const end = calculateEndDate(start, period);

      setFormValues({
        date: formatDateForInput(new Date()), // Default to today
        internName: emp.Name || "",
        universityName: "",
        companyName: emp.Firm || "",
        startDate: start,
        endDate: end,
        internshipInfo: "",
      });
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value };
      // Recalculate end date if start date changes (optional)
      return updated;
    });

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors[name]) delete newErrors[name];
      return newErrors;
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formValues.date) newErrors.date = "Date is required";
    if (!formValues.internName)
      newErrors.internName = "Intern's Full Name is required";
    if (!formValues.universityName)
      newErrors.universityName = "University/College Name is required";
    if (!formValues.companyName)
      newErrors.companyName = "Company/Organization Name is required";
    if (!formValues.startDate) newErrors.startDate = "Start Date is required";
    if (!formValues.endDate) newErrors.endDate = "End Date is required";
    if (!formValues.internshipInfo)
      newErrors.internshipInfo = "Internship Information is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const generateTemplateText = (data) => {
    const formattedDate = formatDate(data.date);
    const formattedStartDate = formatDate(data.startDate);
    const formattedEndDate = formatDate(data.endDate);

    return `
<div style="font-family: Arial, sans-serif; line-height: 1.2rem; font-size: 13px; margin-top : 100px;">
  <p style="text-align: left;"><strong>Date: ${formattedDate}</p></strong>

  <p style="margin-top: 30px;">To Whom It May Concern</p>

  <p style="text-align: left; font-weight: bold; margin: 20px 0;">
    Subject: Internship Completion Letter
  </p>

  <p style="text-align: justify;">
    This is to certify that <strong>${data.internName}</strong>, a student from 
    <strong>${data.universityName}</strong>, has successfully completed their internship at  <strong>${data.companyName}</strong>  <br/>from 
    <strong>${formattedStartDate}</strong> to <strong>${formattedEndDate}</strong>.
  </p>

  <p style="margin-top: 10px;,text-align: justify;">
    During the course of the internship, he was exposed to the various activities in 
    <strong>${data.internshipInfo}</strong>.
  </p>

  <p style="margin-top: 15px; ,text-align: justify;">
    We are confident that the experience gained during this internship will contribute 
    positively to their future career development.
  </p>

  <p style="margin-top: 40px;">With Regards,</p>

  <p style="margin-top: 30px; font-weight: bold;">Dipak Rajput</p>
  <p>Project Manager</p>
</div>
    `;
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const text = generateTemplateText(formValues);
      setGeneratedText(text);
      setErrors({});
      setIsModalOpen(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      date: "",
      internName: "",
      universityName: "",
      companyName: "",
      startDate: "",
      endDate: "",
      internshipInfo: "",
    });
    setErrors({});
    setGeneratedText("");
    setSelectedEmpId("");
  };

  const handleDownloadPdf = async () => {
    const element = pdfContentRef.current;
    if (!element) return;

    const pdf = new jsPDF("p", "mm", "a4");
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Internship_Completion_${formValues.internName || "Intern"}.pdf`);
  };

  if (MemberOldAllLoading) return <Loader />;

  return (
    <div className="min-h-screen p-4 lg:p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold bg-[#122A47] text-white px-4 py-2 inline-block rounded mb-6">
          INTERNSHIP COMPLETION LETTER
        </h1>

        {/* STEP 1: Employee Selection Card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#122A47] mb-4">
            Step 1: Select Employee
          </h2>
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-12 md:col-span-8">
              <select
                value={selectedEmpId}
                onChange={handleEmployeeSelect}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47] bg-white text-gray-900 font-medium"
              >
                <option value="">-- Choose Employee / Intern --</option>
                {MemberOldAllData?.data?.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.Name} {emp.Role ? `(${emp.Role})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-12 md:col-span-4 flex gap-2">
              <button
                disabled={!selectedEmpId}
                onClick={() => setIsModalOpen(true)}
                className={`flex-1 text-white py-2 px-4 rounded-md transition-colors font-semibold ${selectedEmpId
                  ? "bg-[#122A47] hover:bg-[#0c2545]"
                  : "bg-gray-400 cursor-not-allowed"
                  }`}
              >
                Fill Details & Open Template
              </button>
              <button
                onClick={resetForm}
                className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-semibold"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* STEP 2: The Modal (Popup Form) */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-[#122A47] text-white rounded-t-lg">
                <h3 className="text-lg font-bold">Edit Internship Completion Details</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:text-gray-300 text-2xl font-bold"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleModalSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  <InputFieldWithLabel
                    label="Letter Date"
                    name="date"
                    type="date"
                    value={formValues.date}
                    error={errors.date}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="Intern's Full Name (from Database)"
                    name="internName"
                    value={formValues.internName}
                    error={errors.internName}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="University/College Name"
                    name="universityName"
                    placeholder="Enter University/College Name"
                    value={formValues.universityName}
                    error={errors.universityName}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Company Name
                    </label>
                    <select
                      name="companyName"
                      value={formValues.companyName}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Company --</option>
                      <option value="Dynamic Software">Dynamic Software</option>
                      <option value="Dynamic Softlink Pvt Ltd.">Dynamic Softlink Pvt Ltd.</option>
                      {formValues.companyName &&
                        formValues.companyName !== "Dynamic Software" &&
                        formValues.companyName !== "Dynamic Softlink Pvt Ltd." && (
                          <option value={formValues.companyName}>
                            {formValues.companyName} (from DB)
                          </option>
                        )}
                    </select>
                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                    )}
                  </div>

                  <InputFieldWithLabel
                    label="Internship Start Date"
                    name="startDate"
                    type="date"
                    value={formValues.startDate}
                    error={errors.startDate}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="Internship End Date"
                    name="endDate"
                    type="date"
                    value={formValues.endDate}
                    error={errors.endDate}
                    onChange={handleChange}
                  />

                  <div className="col-span-12">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Internship Information / Department
                    </label>
                    <textarea
                      name="internshipInfo"
                      placeholder="Enter the department, project, or work area details (e.g., Web Development using React.js)"
                      value={formValues.internshipInfo}
                      onChange={handleChange}
                      rows={4}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47]"
                    />
                    {errors.internshipInfo && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.internshipInfo}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-[#122A47] text-white py-2 px-4 rounded-md hover:bg-[#0c2545] transition-colors font-medium"
                  >
                    Generate Document
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* STEP 3: Preview and Download Panel */}
        {generatedText && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#122A47]">
                Letter Preview
              </h2>
              <button
                onClick={handleDownloadPdf}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors font-semibold"
              >
                Download PDF
              </button>
            </div>

            <div className="max-w-full overflow-x-auto bg-gray-100 p-4 rounded-lg border border-gray-300">
              <div
                ref={pdfContentRef}
                className="bg-white shadow-lg mx-auto"
                style={{
                  width: "180mm",
                  minHeight: "270mm",
                  padding: "15mm",
                  boxSizing: "border-box",
                }}
              >
                <div
                  className="font-sans text-[13px] text-left leading-relaxed text-gray-900"
                  dangerouslySetInnerHTML={{ __html: generatedText }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternshipCompletionLetter;
